import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import SupplierProfileForm from "@/polymet/components/admin/SupplierProfileForm";
import { supabase } from "@/lib/supabase";
import { showToast } from "@/components/ui/toast";

export default function AdminSupplierEditPage() {
  const { supplierSlug } = useParams<{ supplierSlug: string }>();
  const navigate = useNavigate();
  const [supplierData, setSupplierData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (supplierSlug) {
      loadSupplierData();
    }
  }, [supplierSlug]);

  const loadSupplierData = async () => {
    if (!supplierSlug) return;

    try {
      setLoading(true);
      console.log("Loading supplier data for slug:", supplierSlug);
      
      const { data, error } = await supabase
        .from("supplier_profiles")
        .select("*")
        .eq("slug", supplierSlug)
        .single();

      if (error) {
        console.error("Error loading supplier data:", error);
        throw error;
      }

      if (!data) {
        throw new Error("Supplier not found");
      }

      setSupplierData(data);
      console.log("Supplier data loaded:", data);
    } catch (error) {
      console.error("Error loading supplier data:", error);
      showToast("Failed to load supplier data", "error");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    showToast("Supplier profile updated successfully", "success");
    navigate("/admin");
  };

  const handleDelete = async () => {
    if (!supplierData?.id) return;
    
    if (!window.confirm("Are you sure you want to delete this supplier profile? This action cannot be undone.")) {
      return;
    }

    try {
      const { error } = await supabase
        .from("supplier_profiles")
        .delete()
        .eq("id", supplierData.id);

      if (error) {
        console.error("Error deleting supplier:", error);
        throw error;
      }
      
      showToast("Supplier profile deleted successfully", "success");
      navigate("/admin");
    } catch (error) {
      console.error("Error deleting supplier:", error);
      showToast("Failed to delete supplier profile", "error");
    }
  };

  const handleBack = () => {
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3">Loading supplier data...</span>
      </div>
    );
  }

  if (!supplierData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground mb-4">Supplier profile not found.</p>
            <Button onClick={handleBack}>Back to Admin Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Admin Dashboard
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Edit Supplier Profile
              </h1>
              <p className="text-gray-600 mt-2">
                Modify supplier information and business details for {supplierData.business_name}
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Profile
              </Button>
              <Button
                onClick={handleSave}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <Card>
          <CardHeader>
            <CardTitle>Supplier Profile Information</CardTitle>
            <CardDescription>
              Update the supplier's business information, verification status, and other details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SupplierProfileForm 
              supplierId={supplierData?.id}
              onChange={handleSave}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 